import { useState, useEffect } from "react";
import axios from "axios";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

function PortabilityRcTable({ fpsId, afsoCode, distCode, selectedMonth, selectedYear, onDataLoaded }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [totals, setTotals] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [commodityKeys, setCommodityKeys] = useState([]);

    useEffect(() => {
        if (!selectedMonth || !selectedYear || !fpsId) return;
        setLoading(true);

        axios.post(`${process.env.REACT_APP_URL_SIDE}/PortabilityAbstract/Portabilty_FPS_List`, {
            month: selectedMonth,
            year: selectedYear,
            distCode: distCode,
            afsoCode: afsoCode,
            fpsId
        })
            .then(response => {
                const rawData = response.data.data || [];

                const indexedData = rawData.map((item, index) => ({
                    ...item,
                    originalIndex: index + 1,
                }));

                setTitle(response.data.title || "FPS Report");
                if (onDataLoaded) onDataLoaded();
                setData(indexedData);
                setMessage(response.data.message || "");
                if (indexedData.length > 0) {
                    setCommodityKeys(indexedData[0].commodities.map(c => c.name));
                }
                calculateTotals(indexedData);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching FPS data:", error);
                setData([]);
                setMessage("Internal  error");
                setLoading(false);
            });


    }, [selectedMonth, selectedYear, fpsId]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredData = data.filter(row => {
        const searchTerm = searchQuery.toLowerCase().trim();

        if (!searchTerm) return true;
        const matchesSearch = (value) => {
            if (value === null || value === undefined) return false;
            return value.toString().toLowerCase().includes(searchTerm);
        };

        return (
            matchesSearch(row.rc_id) ||
            matchesSearch(row.scheme) ||
            matchesSearch(row.receipt_id) ||
            matchesSearch(row.logindatetime) ||
            matchesSearch(row.trans_type) ||
            matchesSearch(row.auth_time) ||
            matchesSearch(row.port_fps_id) ||
            matchesSearch(row.fps_id) ||
            matchesSearch(row.originalIndex) ||
            matchesSearch(row.comm_amount) ||

            row.commodities?.some(comm =>
                matchesSearch(comm.name) ||
                matchesSearch(comm.qty)
            ) ||
            matchesSearch(row.portability_details) ||
            matchesSearch(row.district_name) ||
            matchesSearch(row.fps_name)
        );
    });

    const sortedData = [...filteredData].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined || bValue === undefined) return 0;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
            return sortConfig.direction === 'asc'
                ? aValue.toString().localeCompare(bValue.toString())
                : bValue.toString().localeCompare(aValue.toString());
        }
    });

    const calculateTotals = (rows) => {
        const totalsObj = {};
        rows.forEach(row => {
            row.commodities.forEach(c => {
                totalsObj[c.name] = (totalsObj[c.name] || 0) + (parseFloat(c.qty) || 0);
            });
        });
        totalsObj.amount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
        setTotals(totalsObj);
    };

    const exportToExcel = async (tableData, fileName = "FPS_Report.xlsx") => {
        if (!tableData.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report', {
            properties: { tabColor: { argb: 'FFC0000' } },
            views: [{ showGridLines: false }]
        });

        const dataStartRow = 4; // data rows start at row 4 (title row1, header row2, commodity row3)

        const colLetter = (col) => {
            let tmp = col;
            let letter = '';
            while (tmp > 0) {
                let mod = (tmp - 1) % 26;
                letter = String.fromCharCode(65 + mod) + letter;
                tmp = Math.floor((tmp - 1) / 26);
            }
            return letter;
        };

        const fixedLeftHeaders = ["Sl No", "RC No", "Scheme", "Receipt No", "Date"];
        const fixedRightHeaders = ["Amount (Rs.)", "Trans Type", "Auth Trans Time", "Availed Depot", "Tagged Depot"];

        const commodityCount = commodityKeys.length || 0;
        const totalCols = fixedLeftHeaders.length + commodityCount + fixedRightHeaders.length; // total columns

        worksheet.mergeCells(`A1:${colLetter(totalCols)}1`);
        worksheet.getCell('A1').value = title;
        worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getCell('A1').font = { bold: true, size: 16 };

        const headerRow2 = [
            ...fixedLeftHeaders,
            commodityCount > 0 ? "Qty in Kgs" : "",
            ...Array(Math.max(0, commodityCount - 1)).fill(""),
            ...fixedRightHeaders
        ];
        worksheet.addRow(headerRow2);

        const headerRow3 = [
            ...fixedLeftHeaders,
            ...commodityKeys,
            ...fixedRightHeaders
        ];
        worksheet.addRow(headerRow3);

        for (let i = 1; i <= fixedLeftHeaders.length; i++) {
            worksheet.mergeCells(`${colLetter(i)}2:${colLetter(i)}3`);
        }

        if (commodityCount > 0) {
            const startCommCol = fixedLeftHeaders.length + 1;
            const endCommCol = fixedLeftHeaders.length + commodityCount;
            worksheet.mergeCells(`${colLetter(startCommCol)}2:${colLetter(endCommCol)}2`);
        }

        const startRight = fixedLeftHeaders.length + commodityCount + 1;
        const endRight = totalCols;
        for (let i = startRight; i <= endRight; i++) {
            worksheet.mergeCells(`${colLetter(i)}2:${colLetter(i)}3`);
        }

        worksheet.getRow(2).eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE0E0E0' }
            };
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        worksheet.getRow(3).eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE0E0E0' }
            };
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Add data rows
        tableData.forEach((item, index) => {
            const rowArr = [
                item.originalIndex || index + 1,
                item.rc_id,
                item.scheme,
                item.receipt_id,
                item.logindatetime
            ];

            // commodity columns in the same order as commodityKeys
            commodityKeys.forEach(key => {
                const comm = item.commodities.find(c => c.name === key);
                rowArr.push(Number(comm?.qty || 0));
            });

            // fixed right columns
            rowArr.push(
                item.comm_amount,
                item.trans_type,
                item.auth_time,
                item.port_fps_id,
                item.fps_id
            );

            worksheet.addRow(rowArr);
        });

        // After data rows, add totals row with SUM formulas for commodity columns and amount
        const dataEndRow = worksheet.lastRow.number;
        const totalsRowIndex = dataEndRow + 1;

        const totalRowArr = [];
        // Left merge cell text placeholder; we'll merge A: E later and set "Total"
        for (let i = 0; i < fixedLeftHeaders.length; i++) totalRowArr.push(i === 0 ? "Total" : "");

        // Commodity totals formulas
        for (let i = 0; i < commodityCount; i++) {
            const colIndex = fixedLeftHeaders.length + 1 + i; // 1-based
            totalRowArr.push({ formula: `SUM(${colLetter(colIndex)}${dataStartRow}:${colLetter(colIndex)}${dataEndRow})` });
        }

        // Amount total formula
        const amountColIndex = fixedLeftHeaders.length + commodityCount + 1;
        totalRowArr.push({ formula: `SUM(${colLetter(amountColIndex)}${dataStartRow}:${colLetter(amountColIndex)}${dataEndRow})` });

        // placeholders for remaining fixed right columns (no formulas)
        for (let i = 0; i < fixedRightHeaders.length - 1; i++) totalRowArr.push("");

        const lastRow = worksheet.addRow(totalRowArr);
        lastRow.font = { bold: true };

        // Merge the left "Total" cell across first fixedLeftHeaders columns
        worksheet.mergeCells(`A${totalsRowIndex}:${colLetter(fixedLeftHeaders.length)}${totalsRowIndex}`);
        const totalCell = worksheet.getCell(`A${totalsRowIndex}`);
        totalCell.alignment = { vertical: 'middle', horizontal: 'center' };
        totalCell.font = { bold: true };

        // Apply border & alignment to all cells
        worksheet.eachRow({ includeEmpty: false }, (row) => {
            row.eachCell((cell) => {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        // Set dynamic columns width
        const columns = [
            { key: 'slNo', width: 10 },
            { key: 'rcId', width: 25 },
            { key: 'scheme', width: 20 },
            { key: 'receiptNo', width: 20 },
            { key: 'date', width: 20 },
            // commodity widths
            ...commodityKeys.map(() => ({ width: 12 })),
            { key: 'amount', width: 15 },
            { key: 'transType', width: 15 },
            { key: 'authTime', width: 20 },
            { key: 'availedDepot', width: 20 },
            { key: 'taggedDepot', width: 20 }
        ];
        worksheet.columns = columns;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, fileName);
    };


    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    ).map((item, idx) => ({
        ...item,
        virtualIndex: (currentPage - 1) * rowsPerPage + idx + 1
    }));




    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, fpsId, selectedMonth, selectedYear]);



    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
            <CircularProgress />
        </Box></div>
    }

    return (
        <div>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
                    {CircularProgress}
                </Box></div>
            ) : data.length > 0 ? (
                <div>


                    <div align="Right">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                            style={{ padding: "5px", width: "300px", marginBottom: "10px" }}
                        />

                    </div>

                    <table align='center' border={1} cellPadding="5" cellSpacing="0">
                        <thead>
                            <tr className="tableheader" >
                                <th colSpan={16}>
                                    {title}
                                </th>
                            </tr>
                            <tr className="tableheader" >
                                <th rowSpan={2} onClick={() => handleSort('originalIndex')}>Sl No</th>
                                <th rowSpan={2} onClick={() => handleSort('rc_id')}>RC No</th>
                                <th rowSpan={2} onClick={() => handleSort('scheme')}>Scheme</th>
                                <th rowSpan={2} onClick={() => handleSort('receipt_id')}>Receipt No</th>
                                <th rowSpan={2} onClick={() => handleSort('logindatetime')}>Date</th>
                                <th colSpan={commodityKeys.length}>Qty in Kgs</th>
                                <th rowSpan={2} onClick={() => handleSort('comm_amount')}>Amount (Rs.)</th>
                                <th rowSpan={2} onClick={() => handleSort('trans_type')}>Trans Type</th>
                                <th rowSpan={2} onClick={() => handleSort('auth_time')}>Auth Trans Time</th>
                                <th rowSpan={2} onClick={() => handleSort('port_fps_id')}>Availed Depot</th>
                                <th rowSpan={2} onClick={() => handleSort('fps_id')}>Tagged Depot</th>
                            </tr>
                            <tr className="tableheader">
                                {commodityKeys.map(key => <th key={key}>{key}</th>)}

                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.originalIndex}</td>
                                    <td>{row.rc_id || "0"}</td>
                                    <td>{row.scheme || "0"}</td>
                                    <td>{row.receipt_id || "0"}</td>
                                    <td>{row.logindatetime || "0"}</td>
                                    {commodityKeys.map(key => {
                                        const comm = row.commodities.find(c => c.name === key);
                                        return <td style={{ textAlign: 'right' }} key={key}>{Number(comm?.qty || 0).toFixed(2)}</td>;
                                    })}
                                    <td style={{ textAlign: 'right' }} >{row.comm_amount || "0"}</td>
                                    <td>{row.trans_type || "0"}</td>
                                    <td>{row.auth_time || "0"}</td>
                                    <td>{row.port_fps_id || "0"}</td>
                                    <td>{row.fps_id || "0"}</td>

                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="tableheader" >
                                <th colSpan={5}>Total</th>
                                {commodityKeys.map(key => <th style={{ textAlign: 'right' }} key={key}>{Number(totals[key] || 0).toFixed(2)}</th>)}

                                <th style={{ textAlign: 'right' }} >{totals.comm_amount || "0"}</th>
                                <th colSpan={4}></th>

                            </tr>
                            <tr>
                                <td colSpan={16} align="center">
                                    <button className="button button2" onClick={() => exportToExcel(filteredData)}>
                                        Export to Excel
                                    </button>

                                </td>
                            </tr>
                        </tfoot>

                    </table>

                    <div className="pagination" align="Right">
                        <label>Rows per page: </label>
                        <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50} >50</option>
                            <option value={100}>100</option>
                            <option value={filteredData.length}>All</option>
                        </select>

                        <button className="button" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
                        <button className="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>

                        <span> Page {currentPage} of {totalPages} </span>

                        <button className="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                        <button className="button" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>



                    <br></br>
                    <br></br>
                    <br></br>
                </div>
            ) : (
                <div class="mainhead" align="center">
                    {message}
                </div>
            )}
        </div>
    );
}

export default PortabilityRcTable;